import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { triggersQueryKeys } from "@/shared/domain/triggers/hooks";

import {
  type CreateIngestionRuleRequest,
  type IngestionRuleRecord,
  type UpdateIngestionRuleRequest,
} from "../IngestionRuleRecord";

import { ingestionRulesQueryKeys } from "./ingestionRulesQueryKeys";

/**
 * A rule write can change any cached rule answer (which rules match a path,
 * which files a rule would ingest, which defaults the org has) and rewrites or
 * removes the rule's managed trigger, so both caches are invalidated. The
 * written rule's existing-files count is dropped rather than shown while it
 * refetches, since it was counted for the rule before the write.
 */
function useInvalidateAfterWrite(orgId: string) {
  const queryClient = useQueryClient();
  return (record?: IngestionRuleRecord) => {
    if (record !== undefined) {
      queryClient.setQueryData(
        ingestionRulesQueryKeys.byId(orgId, record.rule_id),
        record,
      );
      queryClient.removeQueries({
        queryKey: ingestionRulesQueryKeys.existingFiles(orgId, record.rule_id),
      });
    }
    void queryClient.invalidateQueries({
      queryKey: ingestionRulesQueryKeys.all(orgId),
    });
    void queryClient.invalidateQueries({
      queryKey: triggersQueryKeys.all(orgId),
    });
  };
}

export function useCreateIngestionRule(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const invalidate = useInvalidateAfterWrite(resolvedOrgId);

  return useMutation({
    mutationFn: (request: CreateIngestionRuleRequest) =>
      ingestionRuleService.createRule(request, resolvedOrgId),
    onSuccess: (record) => invalidate(record),
  });
}

export function useUpdateIngestionRule(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const invalidate = useInvalidateAfterWrite(resolvedOrgId);

  return useMutation({
    mutationFn: ({
      ruleId,
      updates,
    }: {
      ruleId: string;
      updates: UpdateIngestionRuleRequest;
    }) => ingestionRuleService.updateRule(ruleId, updates, resolvedOrgId),
    onSuccess: (record) => invalidate(record),
  });
}

export function useDeleteIngestionRule(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();
  const invalidate = useInvalidateAfterWrite(resolvedOrgId);

  return useMutation({
    mutationFn: ({ ruleId }: { ruleId: string }) =>
      ingestionRuleService.deleteRule(ruleId, resolvedOrgId),
    onSuccess: (_, { ruleId }) => {
      queryClient.removeQueries({
        queryKey: ingestionRulesQueryKeys.byId(resolvedOrgId, ruleId),
      });
      invalidate();
    },
  });
}

export function useCreateDefaultIngestionRules(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const invalidate = useInvalidateAfterWrite(resolvedOrgId);

  return useMutation({
    mutationFn: () => ingestionRuleService.createDefaultRules(resolvedOrgId),
    onSuccess: () => invalidate(),
  });
}
